import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useFormField,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { TimeWindows } from './components/TimeWindows'

import { BulkTimeWindowFieldValue } from './types'

export interface BulkTimeWindowProps {
  testId?: string
  label: string
}

const defaultComponentValue: BulkTimeWindowFieldValue = { status: 'mixed' }

export type BulkTimeWindowFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkTimeWindowFieldValue,
> = {
  bulkTimeWindowProps: BulkTimeWindowProps
} & TidalFormBridgeOptions<BulkTimeWindowFieldValue, TargetField, State, AcceptedFormValue>

export function BulkTimeWindowField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = BulkTimeWindowFieldValue,
>(props: BulkTimeWindowFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    bulkTimeWindowProps: { testId, label },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<BulkTimeWindowFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const {
    fieldApi: { validate },
  } = useFormField<TargetField, State>(props.name)

  return (
    <TimeWindows
      testId={testId}
      label={label}
      value={value}
      disabled={fieldDisabled}
      onChange={onChange}
      validate={validate}
      error={showErrorAccent && fieldHasError}
      helperText={errorOrHelperText}
    />
  )
}
